import { useEffect, useMemo, useState } from 'react'
import { DataTablePFSEvent } from 'primereact/datatable'
import { ICountry, IUfinetSelectOption, PagedResponse } from 'ufinet-web-functions'
import { getAqsCountries } from './aqsCountriesRepository'

export const useCostsCountryCatalog = (authData: any) => {
	const [countries, setCountries] = useState<ICountry[]>([])

	useEffect(() => {
		let mounted = true
		getAqsCountries(authData)
			.then((catalog: ICountry[]) => {
				if (!mounted) return
				setCountries(Array.isArray(catalog) ? catalog : [])
			})
			.catch(() => {
				if (!mounted) return
				setCountries([])
			})

		return () => {
			mounted = false
		}
	}, [authData])

	const countryFilterOptions = useMemo<IUfinetSelectOption<string>[]>(() => {
		return countries
			.filter((country) => !!country?.id && !!country?.name)
			.map((country) => ({
				label: country.name,
				value: country.id,
			}))
	}, [countries])

	return { countryFilterOptions }
}

// The table filter value is already the country id, since filterOptions use country.id as value
export const getCountryIdsFromTableFilter = (event: DataTablePFSEvent): string[] => {
	return getSelectFilterValues(event, 'countryName')
}

// Generic extraction for any select-type column filter (e.g. infrastructureType, zoneType): the shared
// Table only fires onFilter with the raw event, it's up to each page to pull out the fields it cares about.
export const getSelectFilterValues = (event: DataTablePFSEvent, field: string): string[] => {
	const value = (event as any)?.filters?.[field]?.value

	if (Array.isArray(value)) {
		return value.filter((it): it is string => typeof it === 'string' && it.trim().length > 0)
	}

	if (typeof value === 'string' && value.trim().length > 0) {
		return [value]
	}

	return []
}

export type MergeCountryFiltersResult = {
	countries: string[]
	// true when the hard filter and the table filter selections don't overlap, meaning results should be empty
	hasImpossibleCountryFilter: boolean
}

export const mergeCountryFilters = (
	hardFilterCountryIds: string[],
	tableFilterCountryIds: string[]
): MergeCountryFiltersResult => {
	if (hardFilterCountryIds.length === 0) {
		return { countries: tableFilterCountryIds, hasImpossibleCountryFilter: false }
	}

	if (tableFilterCountryIds.length === 0) {
		return { countries: hardFilterCountryIds, hasImpossibleCountryFilter: false }
	}

	const tableFilterSet = new Set(tableFilterCountryIds)
	const countries = hardFilterCountryIds.filter((countryId) => tableFilterSet.has(countryId))
	return { countries, hasImpossibleCountryFilter: countries.length === 0 }
}

export const buildEmptyPagedResponse = <T>(pageNumber: number, pageSize: number): PagedResponse<T> => ({
	data: [],
	totalCount: 0,
	pageSize,
	currentPage: pageNumber,
	totalPages: 0,
	hasNext: false,
	hasPrevious: false,
})
